import {
  Component,
  HostListener,
  Inject,
  Injector,
  PLATFORM_ID,
  TransferState,
  afterNextRender,
  makeStateKey,
} from '@angular/core';

import { ProductService } from '@/app/shared/services/product.service';
import { hasProductMenuRequest, isBasicProductGroup, isRecentProductsMenu, ProductMenuRequest } from '@/app/shared/interface/category.interface';
import { CommonService } from '@/app/shared/services/common.service';

import { Breadcrumb } from '@/app/shared/interface/breadcrumb';
import {
  PaginationModel,
  Product,
} from '@/app/shared/interface/product.interface';
import { ActivatedRoute } from '@angular/router';
import { QueryStringService } from '@/app/shared/services/querystring.service';
import { Subscription, firstValueFrom, Subject, from, of } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, take, takeUntil, tap, timeout } from 'rxjs/operators';
import {
  CatetoryQueryString,
  LabelQueryString,
  ManufacturerQueryString,
  SearchUserQueryString,
} from '@/app/shared/filter';
import { Router, NavigationEnd } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { AuthService, IS_AUTHENTICATED, TOKEN } from '@/app/shared/services/auth.service';
import { Token } from '@angular/compiler';
import { ConfigurationV2Service } from '@/app/shared/services/configuration-v2.service';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import {
  ListBanners,
  ScreenListBanner,
} from '@/app/shared/interface/banner.interface';
import {
  BANNER_TYPES,
  SCREENS_ID,
} from '@/app/shared/constants/common.constant';
import { AccountService } from '@/app/shared/services/account.service';
import { User } from '@/app/shared/interface/user.interface';
import { LISTING_SLUG_PARAMS, SeoService } from '@/app/shared/services/seo.service';
import { CategoryService } from '@/app/shared/services/category.service';

// H1 + breadcrumb SSR đã render, để lần render đầu trên browser dùng lại y nguyên (không nháy về giá trị mặc định
// trong lúc chờ API). params = JSON queryParams lúc SSR: chỉ dùng khi trùng URL hiện tại.
const LISTING_HEADING = makeStateKey<{ params: string, heading: string, breadcrumb: Breadcrumb }>('productListingHeading');

@Component({
  selector: 'app-product',
  templateUrl: './product.component.html',
  styleUrl: './product.component.scss',
})
export class ProductComponent {
  private changeDataSubcription?: Subscription;
  private destroy$ = new Subject<void>();
  private requestSeq = 0; // Sequence number to track the latest request
  isBrowser: boolean = false;
  isVipCustomer: boolean = false;
  public breadcrumb: Breadcrumb = {
    title: 'Sản Phẩm',
    items: [{ label: 'Sản Phẩm', active: true }],
  };

  public gridCol: string;
  public skeletonLoader: boolean = true;
  public listingError = false;
  public skeletonItems = Array.from({ length: 40 }, (_, index) => index);

  // public gridClass: string = 'row g-sm-4 g-3 row-cols-xxl-4 row-cols-xl-3 row-cols-lg-2 row-cols-md-3 row-cols-2 product-list-section';
  public gridClass: string =
    'row product-list-section row-cols-xl-5 row-cols-lg-4 row-cols-md-3 row-cols-2 my-0';

  public filter: any = {
    page: 1, // Current page number
    status: 1,
    field: '',
    price: '',
    category: '',
    tag: '',
    sort: '', // ASC, DSC
    sortBy: '',
    rating: '',
    attribute: '',
  };

  public products: Product[] = [];
  public productsExtend: Product[] = [];
  public productsDisplay: Product[] = [];
  public productsExtendDisplay: Product[] = [];
  public categories: any[] = [];
  // public pagination: Paginate;

  _currentPage: number = 1;
  _paginateRange: number = 7;
  public bodyExtract = {
    limit: 20,
    searchTerm: '',
    slugs: [],
  };
  bodyProduct = {
    limit: 20,
    offset: 0,
  };
  public productBanner: ListBanners | undefined;
  public offCanvasMenu: boolean = false;
  public labelQueryString: LabelQueryString;
  public categoryQueryString: CatetoryQueryString;
  public manufacturerQueryString: ManufacturerQueryString;
  public isAuthenticated: boolean = false;
  public searchQuery: SearchUserQueryString = new SearchUserQueryString();

  isListView: boolean = false;
  /** Phần gợi ý tìm kiếm đang hiển thị (từ app-search-suggestions): hiện tiêu đề cho kết quả thường, ẩn thông báo "không tìm thấy". */
  public hasSuggestions: boolean = false;
  /** Đang chờ phản hồi gợi ý: hiện skeleton thay vì "Không tìm thấy" (tránh nháy trống rồi mới hiện gợi ý). */
  public suggestionPending: boolean = false;
  recentProducts: Product[] = [];

  hasFilters: boolean = false;

  title: string = 'Danh Sách Sản Phẩm';
  heading: string = this.title;
  // Cây danh mục tải trên server (chỉ SSR); browser dùng commonService.productCategoriesV2.
  private ssrCategoryTree?: any[];
  // NSX của ?manufacturer= không có trong cây danh mục, tra bằng API (loadListingManufacturers) cho params hiện tại.
  private listingManufacturers: any[] = [];
  // Danh sách sản phẩm đã tải theo params hiện tại (nguồn tên label cho SEO/breadcrumb); undefined = đang tải / API lỗi.
  private loadedProducts?: Product[];
  // Đã tải xong (thành công hoặc lỗi) danh sách theo params hiện tại => được phép cập nhật H1/breadcrumb/SEO.
  private listingReady = false;

  // Số cột quyết định vị trí chèn banner. Lần render đầu dùng giá trị cố định cho cả server và browser
  // (server không có window) để DOM khớp khi hydrate; sau render đầu mới tính theo window.
  private columns = 5;
  private columnsSynced = false;

  // Trên mobile, thanh địa chỉ ẩn/hiện khi scroll => 'resize' (chỉ đổi chiều cao) bắn liên tục.
  // Chỉ dựng lại danh sách khi số cột thực sự đổi, nếu không mọi product box bị tạo lại => ảnh nhấp nháy.
  @HostListener('window:resize')
  onResize() {
    const columns = this.getColumns();
    if (columns === this.columns) return;
    this.columns = columns;
    this.productsDisplay = this.buildDisplayList(this.products);
    this.productsExtendDisplay = this.buildDisplayList(this.productsExtend);
  }

  constructor(
    private productService: ProductService,
    private commonService: CommonService,
    private queryStringService: QueryStringService,
    private router: Router,
    private transferState: TransferState,
    private authService: AuthService,
    private route: ActivatedRoute,
    private configurationV2Service: ConfigurationV2Service,
    private accountService: AccountService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private cookieService: SsrCookieService,
    private seoService: SeoService,
    private injector: Injector,
    private categoryService: CategoryService,
  ) { }
  
  async ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.isBrowser = true;
      this.isAuthenticated = this.authService.isAuthenticated();
      this.accountService.getUserData.subscribe((res) => {
        if (res && res.roles?.length) {
          this.isVipCustomer = res.roles.some((role) => role.roleName.toLowerCase() === 'vip' || role.roleName.toLowerCase() === 'khcl');
        }
      });

      // Lần render đầu giữ đúng H1/breadcrumb của HTML SSR; chỉ đổi khi đã tải xong data (xem updateListingState).
      const ssrHeading = this.transferState.get(LISTING_HEADING, null);
      this.transferState.remove(LISTING_HEADING);
      if (ssrHeading?.params === JSON.stringify(this.route.snapshot.queryParams)) {
        this.heading = ssrHeading.heading;
        this.breadcrumb = ssrHeading.breadcrumb;
      }

      // Khách: danh mục có sẵn đồng bộ (transfer cache). Đã đăng nhập: Layout mới bắt đầu gọi API => phải chờ, nếu không
      // category/manufacturer không đổi được slug -> id (request gửi "categories": [] => lọc sai), tên trong H1 chưa tra được.
      // Timeout: API danh mục lỗi vẫn tải danh sách như trước.
      const categoryTree$ = this.commonService.getCategoriesV2.pipe(
        filter(Boolean),
        take(1),
        timeout({ first: 5000, with: () => of(null) }),
      );

      this.changeDataSubcription = this.route.queryParams
      .pipe(
        // Gọi ngay khi vừa vào page
        startWith(this.route.snapshot.queryParams),

        // Tránh gọi trùng khi queryParams emit 2 lần giống nhau
        map((params) => JSON.stringify(params)),
        distinctUntilChanged(),

        tap(() => {
          ++this.requestSeq;
          // Không cập nhật H1/breadcrumb/SEO ở đây: chưa có data => giá trị tạm, bị ghi đè lại sau khi tải (nháy heading).
          this.listingReady = false;
          this.loadedProducts = undefined;
          this.queryStringService.startFetchingData();
          this.readQueryStrings();
        }),

        switchMap(() => {
          const params = this.route.snapshot.queryParamMap;
          const needsCategoryTree = LISTING_SLUG_PARAMS.some((param) => params.has(param));
          return (needsCategoryTree ? categoryTree$ : of(null)).pipe(
            switchMap(() => from(this.loadListingManufacturers(this.commonService.productCategoriesV2))),
            switchMap(() => from(this.loadListing())),
            tap(() => {
              const dataQueryString = this.queryStringService.queryString
                .filter((e) => e.isDataFilter)
                .filter((e) => e.value !== '');

              this.hasFilters = dataQueryString.length > 0;
            }),
          );
        }),

        takeUntil(this.destroy$),
      )
      .subscribe();

      if (this.commonService.productCategoriesV2?.length) {
        this.categories = this.commonService.productCategoriesV2.filter(isBasicProductGroup);
      } else {
        let sub = this.commonService.getCategoriesV2.subscribe((data) => {
          if (data) {
            this.categories = data.filter(isBasicProductGroup);
            this.queryStringService.startFetchingData();
            // Danh sách đã tải trước khi có danh mục (timeout / URL không cần danh mục): cập nhật tên theo danh mục.
            if (this.listingReady) {
              this.updateListingState();
            }
            sub?.unsubscribe();
          }
        });
      }

      this.productService.recentProductsView$.subscribe((products) => {
        this.recentProducts = products;
      });
      this.getBanner();
    }
    else {
      // Title/H1/breadcrumb/robots theo danh mục phải có ngay trong HTML SSR (crawler không chạy JS vẫn thấy).
      const params = this.route.snapshot.queryParamMap;
      if (LISTING_SLUG_PARAMS.some((param) => params.has(param))) {
        this.ssrCategoryTree = await this.getCategoryTreeForSsr();
        this.categories = (this.ssrCategoryTree ?? []).filter(isBasicProductGroup);
        await this.loadListingManufacturers(this.ssrCategoryTree);
      }
      this.readQueryStrings();

      // SSR danh sách sản phẩm cho khách chưa đăng nhập (crawler). Bỏ qua khi:
      // - đã đăng nhập: browser gọi API member, data khác bản guest;
      // - tìm kiếm (searchTerm / s): trang noindex + chặn trong robots.txt, crawler không cần nội dung, còn API tìm kiếm
      //   chậm (đo ~3.7s) => SSR chỉ trả khung trang, browser tự tải kết quả;
      // - filter category/manufacturer mà không tải được danh mục: không đổi được slug -> id như browser.
      const isSearch = params.has('searchTerm') || params.has('s');
      const needsCategoryTree = params.has('category') || params.has('manufacturer');
      if (!this.transferState.get(IS_AUTHENTICATED, false) && !isSearch && (!needsCategoryTree || this.ssrCategoryTree)) {
        await this.loadListing(); // cập nhật H1/breadcrumb/SEO khi xong
      } else {
        await this.loadLabelNamesForSsr();
        this.updateListingState();
      }
      // H1/breadcrumb ở trên SSR chỉ set một lần, sau khi đủ data (title/meta trong <head> cũng vậy).
      this.transferState.set(LISTING_HEADING, {
        params: JSON.stringify(this.route.snapshot.queryParams),
        heading: this.heading,
        breadcrumb: this.breadcrumb,
      });
    }
  }

  // SSR không tải danh sách sản phẩm (đã đăng nhập, tìm kiếm...) nhưng URL có label không nằm trong danh mục: tên label chỉ có
  // trong product.labels => gọi API guest limit 1 cho từng label (~115ms) chỉ để lấy tên cho title/H1/breadcrumb.
  // User đã đăng nhập: transfer cache tắt => response không nhúng vào HTML, browser vẫn tự gọi API member.
  private async loadLabelNamesForSsr(): Promise<void> {
    const slugs = String(this.route.snapshot.queryParams['label'] ?? '').split(',').filter(slug => slug)
      .filter(slug => !this.seoService.getCategoryBySlug(this.ssrCategoryTree ?? [], slug));
    if (!slugs.length) {
      return;
    }
    try {
      const results = await Promise.all(slugs.map(slug => firstValueFrom(
        this.productService.getProductsPaginationforGuest({ limit: 1, offset: 0, labels: [slug] }).pipe(timeout(3000)),
      )));
      this.loadedProducts = results.flatMap(res => res?.products ?? []);
    } catch {
      // Lỗi/timeout: giữ title theo danh mục / STATIC_LISTING_PAGES, không coi label là rỗng (không noindex nhầm).
    }
  }

  // NSX trong bộ lọc sidebar lấy từ API retrieve-manufacturer (CategoryFilter, chỉ trên browser, sau khi trang tải) — đa số không
  // có trong cây danh mục. Reload / crawler vào ?manufacturer=<slug> đó thì không đổi được slug -> tên NSX: request không lọc
  // (ra mọi NSX) và H1/title thiếu tên. Tra trước khi tải sản phẩm (SSR + browser, cùng body => khách trúng transfer cache):
  // searchTerm = slug (~90ms; API tìm theo tên nên vài slug không khớp, vd. an-o = "Ấn Độ") => còn thiếu thì lấy cả danh sách.
  private async loadListingManufacturers(tree: any[] | undefined): Promise<void> {
    this.listingManufacturers = [];
    const param = this.route.snapshot.queryParams['manufacturer'];
    const known = [...(tree ?? []), ...(this.commonService.manuCategoriesV2 ?? [])];
    const slugs = String(param ?? '').split(',').filter(slug => slug)
      .filter(slug => !this.seoService.getCategoryBySlug(known, slug));
    if (!slugs.length) {
      return;
    }
    const pick = (items: any[]) => slugs
      .map(slug => items.find(item => item.slug?.toLowerCase() === slug.toLowerCase()))
      .filter(Boolean);
    try {
      if (isRecentProductsMenu(this.configuredProductMenu)) {
        const items = await firstValueFrom(this.categoryService.getRecentProductManufacturers().pipe(timeout(10000)));
        if (this.route.snapshot.queryParams['manufacturer'] === param) {
          this.listingManufacturers = pick(items);
        }
        return;
      }
      const results = await Promise.all(slugs.map(slug => firstValueFrom(
        this.categoryService.getManuCategories({ searchTerm: slug }).pipe(timeout(3000)),
      )));
      let found = pick(results.flatMap(res => res?.items ?? []));
      if (found.length < slugs.length) {
        const all = await firstValueFrom(this.categoryService.getManuCategories({ limit: 1000 }).pipe(timeout(3000)));
        found = pick(all?.items ?? []);
      }
      // Đã chuyển sang NSX khác trong lúc chờ: lượt tra mới tự đặt lại.
      if (this.route.snapshot.queryParams['manufacturer'] === param) {
        this.listingManufacturers = found;
      }
    } catch {
      // Lỗi/timeout: tải như trước (không lọc được NSX), H1 không có tên NSX.
    }
  }

  // Cây dùng đổi slug -> id (buildFetchingModel) và tra tên (SEO): cây danh mục + NSX tra thêm.
  // Không có NSX tra thêm => như cũ (server: ssrCategoryTree; browser: undefined = commonService.productCategoriesV2).
  private get listingTree(): any[] | undefined {
    if (!this.listingManufacturers.length) {
      return this.ssrCategoryTree;
    }
    return [...(this.ssrCategoryTree ?? this.commonService.productCategoriesV2 ?? []), ...this.listingManufacturers];
  }

  private readQueryStrings() {
    this.labelQueryString = this.queryStringService.getQueryString(
      new LabelQueryString(),
    );
    this.categoryQueryString = this.queryStringService.getQueryString(
      new CatetoryQueryString(),
    );
    this.searchQuery = this.queryStringService.getQueryString(
      new SearchUserQueryString(),
    );
    this.manufacturerQueryString = this.queryStringService.getQueryString(
      new ManufacturerQueryString(),
    );
  }

  // Điểm duy nhất cập nhật H1 + breadcrumb + title/meta/robots/BreadcrumbList: gọi sau khi danh sách theo params hiện tại
  // đã tải xong (và đã có danh mục nếu URL cần) => mỗi lần đổi params chỉ đổi một lần, không qua giá trị tạm.
  // listingTree: xem getter (browser không có NSX tra thêm: undefined => SeoService dùng commonService.productCategoriesV2).
  private updateListingState() {
    this.listingReady = true;
    this.updateBreadcrumb();
    this.heading = this.seoService.updateListingSeo(
      this.title, this.route.snapshot.queryParams, this.listingTree, this.loadedProducts,
    );
  }

  // Danh mục cho SEO khi SSR. Chỉ giữ cục bộ, KHÔNG setCategoriesV2: Menu/CategoryFilter subscribe danh mục sẽ
  // render thêm DOM + gọi thêm API ngay trên server, lệch với lần render đầu trên browser của user đã đăng nhập.
  // Khách chưa đăng nhập: transfer cache mang response sang browser => Layout dùng lại, không gọi API lần hai.
  private async getCategoryTreeForSsr(): Promise<any[] | undefined> {
    try {
      const res = await firstValueFrom(this.categoryService.getCategoriesV2().pipe(timeout(3000)));
      return Array.isArray(res) ? res : undefined;
    } catch {
      // Lỗi/timeout: vẫn render; title không có tên danh mục, và không bị noindex nhầm.
      return undefined;
    }
  }

  // getExtractProduct + xử lý lỗi API: lỗi không làm chết stream queryParams (đổi filter sau đó vẫn tải) và H1/SEO vẫn được
  // cập nhật theo danh mục; loadedProducts = undefined (chưa biết) => không coi label là rỗng (không noindex nhầm).
  private async loadListing() {
    const listing = this.getExtractProduct();
    const requestId = this.requestSeq;
    try {
      await listing;
    } catch {
      if (requestId === this.requestSeq) {
        this.listingError = true;
        this.skeletonLoader = false;
        this.loadedProducts = undefined;
        this.updateListingState();
      }
    }
  }

  async getExtractProduct() {
    const requestId = ++this.requestSeq;
    this.listingError = false;

    let body = this.queryStringService.buildFetchingModel(
      this.bodyProduct.limit,
      this.listingTree,
    );
    this.skeletonLoader = true;
    body.searchTerm = body.searchTerm!;

    this.products = [];
    this.productsExtend = [];
    this.productsDisplay = [];
    this.productsExtendDisplay = [];
    const token = this.transferState.get(TOKEN, '');
    if (this.bodyExtract.searchTerm && token && !this.configuredProductMenu) {
      const res = await firstValueFrom(
        this.productService.searchProductsForMember(
          JSON.stringify(this.bodyExtract),
        ),
      );
      if (requestId !== this.requestSeq) return;

      if (res) {
        this.products = res.products;
        if (this.products.length < 5) {
          await this.getProducts(requestId);
          if (requestId !== this.requestSeq) return;
          this.productsExtend = this.productsExtend.filter(
            (item) => !this.products.some((i) => i.id === item.id),
          );
        }
        let pagination: PaginationModel = {
          limit: body.limit,
          offset: body.offset,
          searchTerm: body.searchTerm,
          totalItems: res.total,
          sortBy: body.sortBy,
          category: '',
        };
        this.queryStringService.changePagination(pagination);
        this.skeletonLoader = false;

        this.productsExtend = this.productsExtend.map((product) => {
          product.currentTier = product.tieredPrices.find(
            (x: any) => x.pricingTableId == product.appliedPricingTable.id,
          );
          return product;
        });
      }

      this.skeletonLoader = false;
    } else {
      await this.getProducts(requestId);
      if (requestId !== this.requestSeq) return;
      this.productsExtend = this.productsExtend.filter(
        (item) => !this.products.some((i) => i.id === item.id),
      );
      this.productsExtend = this.productsExtend.map((product) => {
        product.currentTier = product.tieredPrices?.find(
          (x: any) => x.pricingTableId == product.appliedPricingTable?.id,
        );
        return product;
      });
    }
    if (requestId !== this.requestSeq) return;
    // Tên label trong title/H1/breadcrumb lấy từ product.labels của chính danh sách này (không có data => slug).
    // Kết quả theo filter nằm ở productsExtend (getProducts); products chỉ có khi member tìm kiếm.
    this.loadedProducts = [...this.products, ...this.productsExtend];
    this.updateListingState();
    this.productsDisplay = this.buildDisplayList(this.products);
    this.productsExtendDisplay = this.buildDisplayList(this.productsExtend);
    this.skeletonLoader = false;

    // Lần dựng đầu trên browser giữ số cột giống SSR để hydration dùng lại đúng các view đã render;
    // chỉ sau khi danh sách đó render xong mới tính lại số cột theo window.
    if (this.isBrowser && !this.columnsSynced) {
      this.columnsSynced = true;
      afterNextRender(() => this.onResize(), { injector: this.injector });
    }
  }

  private get configuredProductMenu(): ProductMenuRequest | undefined {
    const params = this.route.snapshot.queryParams;
    const slug = String(params['category'] || params['label'] || '').split(',').filter(Boolean);
    if (slug.length !== 1) return undefined;
    const menu = this.seoService.getCategoryBySlug(
      this.ssrCategoryTree ?? this.commonService.productCategoriesV2 ?? [], slug[0],
    );
    return menu && menu.visible !== false && hasProductMenuRequest(menu) ? menu : undefined;
  }

  retryListing() {
    void this.loadListing();
  }

  async getBanner() {
    this.configurationV2Service.appBanner$.subscribe({
      next: (res: ScreenListBanner[]) => {
        if (res && res.length > 0) {
          this.productBanner =
            res
              .find(
                (item: ScreenListBanner) =>
                  item.screenId === SCREENS_ID.PRODUCT,
              )
              ?.listBanners.find(
                (group: ListBanners) => group.type === BANNER_TYPES.PRODUCT,
              ) || undefined;
        }
      },
    });
  }

  async getProducts(requestId = this.requestSeq) {
    const menu = this.configuredProductMenu;
    let body = this.queryStringService.buildFetchingModel(
      menu?.query?.limit ?? this.bodyProduct.limit,
      this.listingTree,
    );

    this.skeletonLoader = true;
    this.productsExtend = [];

    const token = this.cookieService.get('token');

    // Resolve listing slugs to Manufacturer.Name; HttpClient encodes each repeated parameter.
    if (menu) {
      body.limit = menu.query?.limit ?? 20;
      const page = Math.max(1, Number(this.route.snapshot.queryParams['page']) || 1);
      body.offset = (page - 1) * body.limit;
    }
    const manufacturerTree = [
      ...(this.commonService.manuCategoriesV2 ?? []),
      ...(this.listingTree ?? this.commonService.productCategoriesV2 ?? []),
    ];
    const manufacturers: string[] = isRecentProductsMenu(menu)
      ? (this.manufacturerQueryString?.originalValue ?? [])
          .flatMap(slug => this.seoService.getCategoryBySlug(manufacturerTree, slug)?.query?.manufacturers ?? [])
          .filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
      : [];
    const res = await firstValueFrom(menu
      ? this.productService.getMenuProducts(menu, body.offset, manufacturers)
      : token
        ? this.productService.getProductsPaginationForMember(body)
        : this.productService.getProductsPaginationforGuest(body));
    if (requestId !== this.requestSeq) return;

    this.productsExtend = (res.products ?? []).map((product) => {
      product.currentTier = product.tieredPrices?.find(
        (x: any) => x.pricingTableId == product.appliedPricingTable?.id,
      );
      return product;
    });
    this.queryStringService.changePagination({
      limit: body.limit,
      offset: body.offset,
      searchTerm: menu ? '' : body.searchTerm,
      totalItems: res.total,
      sortBy: menu ? '' : body.sortBy,
      category: '',
    });
  }

  queryStringBuilder(event: any) {
    const newLabel = new LabelQueryString(event);

    // Lấy toàn bộ các query string đang có
    const currentQueryStrings = this.queryStringService.queryString;

    // Giữ lại những cái không phải label
    const otherQueryStrings = currentQueryStrings.filter(
      (qs) => !(qs instanceof LabelQueryString),
    );

    // Gộp lại
    const mergedQueryStrings = [...otherQueryStrings, newLabel];

    // Gọi thay đổi
    this.queryStringService.changeQueryString(mergedQueryStrings);
  }

  get currentSearchTerm(): string {
    const params = this.route.snapshot.queryParamMap;
    return (params.get('searchTerm') ?? params.get('s') ?? '').trim();
  }

  setGridClass(gridClass: any) {
    this.gridClass = gridClass.class;
    this.isListView = gridClass.isListView;
  }

  showSideBar(e: boolean) {
    this.offCanvasMenu = e;
  }

  updateBreadcrumb() {
    this.breadcrumb.items = [];
    const categoryValue = this.categoryQueryString?.value;
    const labelValue = this.labelQueryString?.value;
    const manufacturerValue = this.manufacturerQueryString?.value;
    const categoryOriginalValue = this.categoryQueryString?.originalValue;
    const labelOriginalValue = this.labelQueryString?.originalValue;

    this.breadcrumb.items.push({
      label: 'Sản Phẩm',
      active: !(categoryValue || labelValue || manufacturerValue),
      url: `/product`,
    });

    if (categoryValue || labelValue) {
      const isCategory = !!categoryValue;

      const queryKey = isCategory ? 'category' : 'label';
      const queryValue = isCategory ? categoryValue : labelValue;

      const label = isCategory
        ? this.getCategoryNamesBySlugs(categoryOriginalValue, this.categories)
        : (labelOriginalValue ?? [])
          .map((slug: string) => this.seoService.getListingName('label', slug, this.categories, this.loadedProducts).name)
          .filter(Boolean).join(',');

      this.breadcrumb.items.push({
        label: `${label}`,
        active: manufacturerValue ? false : true,
        url: `/product?${queryKey}=${encodeURIComponent(queryValue)}`
      });
    }

    if (manufacturerValue) {
      this.breadcrumb.items.push({
        label: `Lọc`,
        active: true,
      });
    }
  }

  getCategoryNamesBySlugs(slugs: string[], categories: any[]): string {
    if (!slugs?.length || !categories?.length) {
      return '';
    }
    const result: string[] = [];
    const findCategories = (items: any[]) => {
      if (!items || !Array.isArray(items)) return;
      items.forEach((item) => {
        if (slugs.includes(item.slug)) {
          result.push(item.name);
        }

        if (item.children?.length) {
          findCategories(item.children);
        }
      });
    };
    findCategories(categories);
    return [...new Set(result)].join(',');
  }

  getColumns() {
    const width = window.innerWidth;
    if (width >= 1600) return 5;
    if (width >= 1360) return 4;
    if (width >= 768) return 3;
    return 2;
  }

  buildDisplayList(products: any[]) {
    const columns = this.columns;
    const result: any[] = [];

    products.forEach((product, index) => {
       product.progressLabel = this.commonService.getProgressLabel(
        product,
      );
      result.push({
        ...product,
        type: 'product',
      });

      if ((index + 1) % columns === 0 && index !== products.length - 1) {
        result.push({
          type: 'banner',
          afterId: product.id,
        });
      }
    });

    return result;
  }

  // buildDisplayList tạo object mới mỗi lần => cần key ổn định để ngFor giữ lại DOM (ảnh) của product box.
  trackByDisplayItem(index: number, item: any) {
    return item.type === 'banner' ? `banner-${item.afterId}` : item.sku ?? item.productID ?? index;
  }

  trackByProduct(index: number, product: any) {
    return product.sku ?? product.productID ?? index;
  }

  ngOnDestroy() {
    ++this.requestSeq;
    this.changeDataSubcription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
